import { v4 as uuidv4 } from 'uuid';
import deepEqual from 'fast-deep-equal';
import { createCachedSelector, FlatMapCache } from 're-reselect';
import { createSelectorCreator, defaultMemoize } from 'reselect';
import {
  markdownToDraftRaw,
  draftRawToMarkdown,
} from '../utils/contracts/markdownConversion';
import {
  wordIndexFromText,
  searchForDefinitionTermMatches,
} from '../utils/contracts/contractDefinitionsSearch';
import {
  rehydrateContractClauses,
  rehydrateContractDefinitions,
  serializeContractClauses,
  serializeContractDefinitions,
} from '../utils/contracts/contractProseDataTransform';
import { accountSlice } from './Account';
import Service from './Service';

// Document IDs (internal)
const documentIdFromContractViewIdForClauses = (contractViewId) =>
  `B-${contractViewId || ''}`;
const documentIdFromContractViewIdForDefinitions = (contractViewId) =>
  `D-${contractViewId || ''}`;
const contractViewIdFromDocumentId = (documentId) => documentId?.slice?.(2);

const SLICE = 'contractProse';

const initialState = {
  editStateByDocumentId: {},
  localEditorStateByDocumentId: {},
  currentContractViewId: null,
  stateForCurrentContractViewId: {
    titleTextEditsByClauseId: {},
    bodyTextEditsByClauseId: {},
    cursorSelection: {
      inClauseId: null,
      linkToClauseId: null,
      selectionOffsetInBody: undefined,
    },
    linkHover: {
      inClauseId: null,
      linkToClauseId: null,
      linkStartOffsetInBody: undefined,
      linkEndOffsetInBody: undefined,
    },
  },
};

function clearStateOnLogOut() {
  return initialState;
}

//
// Reducer
//

const COLLAPSE_CLAUSE = 'tokenized/outliner/COLLAPSE_CLAUSE';
const COLLAPSE_CLAUSES = 'tokenized/outliner/COLLAPSE_CLAUSES';
const CLEAR_EDIT_STATE = 'tokenized/outliner/CLEAR_EDIT_STATE';
const PREPARE_FOR_EDITING = 'tokenized/outliner/PREPARE_FOR_EDITING';
const HIDE_FIRST_CHAPTER = 'tokenized/outliner/HIDE_FIRST_CHAPTER';
const SHOW_FIRST_CHAPTER = 'tokenized/outliner/SHOW_FIRST_CHAPTER';
const DIVIDE_INTO_CHAPTERS = 'tokenized/outliner/DIVIDE_INTO_CHAPTERS';
const ADD_CHAPTER_BEFORE = 'tokenized/outliner/ADD_CHAPTER_BEFORE';
const ADD_CHAPTER_AFTER = 'tokenized/outliner/ADD_CHAPTER_AFTER';
const UPDATE_CLAUSE = 'tokenized/outliner/UPDATE_CLAUSE';
const UPDATE_CLAUSES = 'tokenized/outliner/UPDATE_CLAUSES';
const MOVE_CURSOR_FROM_CLAUSE = 'tokenized/outliner/MOVE_CURSOR_FROM_CLAUSE';
const INSERT_CLAUSES_AFTER = 'tokenized/outliner/INSERT_CLAUSES_AFTER';
const INDENT_CLAUSE_RIGHT = 'tokenized/outliner/INDENT_CLAUSE_RIGHT';
const INDENT_CLAUSE_LEFT = 'tokenized/outliner/INDENT_CLAUSE_LEFT';
const DELETE_CLAUSE = 'tokenized/outliner/DELETE_CLAUSE';
const EDIT_TITLE_TEXT = 'tokenized/outliner/EDIT_TITLE_TEXT';
const EDIT_BODY_TEXT = 'tokenized/outliner/EDIT_BODY_TEXT';
const SET_CURSOR_SELECTION = 'tokenized/outliner/SET_CURSOR_SELECTION';
const CLEAR_CURSOR_SELECTION = 'tokenized/outliner/CLEAR_CURSOR_SELECTION';
const SET_BODY_LINK_HOVER = 'tokenized/outliner/SET_BODY_LINK_HOVER';
const CLEAR_BODY_LINK_HOVER = 'tokenized/outliner/CLEAR_BODY_LINK_HOVER';

export const contractProseSlice = {
  name: SLICE,
  reducer: (state = initialState, action) => {
    switch (action.type) {
      case `${accountSlice.actions.logOut}`:
        return clearStateOnLogOut(state);

      case COLLAPSE_CLAUSE: {
        const { documentId, clauseId, isCollapsed } = action.payload;
        if (!documentId || !clauseId) {
          return state;
        }
        return {
          ...state,
          localEditorStateByDocumentId: {
            ...state.localEditorStateByDocumentId,
            [documentId]: {
              ...state.localEditorStateByDocumentId?.[documentId],
              [clauseId]: {
                ...state.localEditorStateByDocumentId?.[documentId]?.[clauseId],
                isCollapsed: !!isCollapsed,
              },
            },
          },
        };
      }

      case COLLAPSE_CLAUSES: {
        const { documentId, clauseIds, isCollapsed } = action.payload;
        if (!documentId || !clauseIds?.length) {
          return state;
        }
        return {
          ...state,
          localEditorStateByDocumentId: {
            ...state.localEditorStateByDocumentId,
            [documentId]: clauseIds.reduce(
              (prevCollapsed, clauseId) => ({
                ...prevCollapsed,
                [clauseId]: {
                  ...state.localEditorStateByDocumentId?.[documentId]?.[
                    clauseId
                  ],
                  isCollapsed: !!isCollapsed,
                },
              }),
              state.localEditorStateByDocumentId?.[documentId],
            ),
          },
        };
      }

      case CLEAR_EDIT_STATE: {
        const { documentId } = action.payload;
        if (!documentId) {
          return state;
        }
        return {
          ...state,
          editStateByDocumentId: {
            ...state.editStateByDocumentId,
            [documentId]: {},
          },
          currentContractViewId: null,
          stateForCurrentContractViewId: {},
        };
      }

      case PREPARE_FOR_EDITING:
        return editStateReducer(editsForPrepareForEditing, state, action);

      case SHOW_FIRST_CHAPTER:
        return editStateReducer(editsForShowFirstChapter, state, action);

      case HIDE_FIRST_CHAPTER:
        return editStateReducer(editsForHideFirstChapter, state, action);

      case DIVIDE_INTO_CHAPTERS:
        return editStateReducer(editsForDivideIntoChapters, state, action);

      case ADD_CHAPTER_BEFORE:
        return editStateReducer(editsForAddChapterBefore, state, action);

      case ADD_CHAPTER_AFTER:
        return editStateReducer(editsForAddChapterAfter, state, action);

      case UPDATE_CLAUSE:
        return editStateReducer(editsForUpdateClause, state, action);

      case UPDATE_CLAUSES:
        return editStateReducer(editsForUpdateClauses, state, action);

      case MOVE_CURSOR_FROM_CLAUSE:
        return editStateReducer(editsForMoveCursorFromClause, state, action);

      case INSERT_CLAUSES_AFTER:
        return editStateReducer(editsForInsertClausesAfter, state, action);

      case INDENT_CLAUSE_RIGHT:
        return editStateReducer(editsForIndentClauseRight, state, action);

      case INDENT_CLAUSE_LEFT:
        return editStateReducer(editsForIndentClauseLeft, state, action);

      case DELETE_CLAUSE:
        return editStateReducer(editsForDeleteClause, state, action);

      case EDIT_TITLE_TEXT: {
        const { documentId, clauseId, text } = action.payload;
        if (!documentId || !clauseId) {
          return state;
        }
        const contractViewId = contractViewIdFromDocumentId(documentId);
        const stateForCurrentContractViewId =
          state.currentContractViewId === contractViewId
            ? state.stateForCurrentContractViewId
            : {};
        if (
          stateForCurrentContractViewId.titleTextEditsByClauseId?.[clauseId] ===
          text
        ) {
          // Don’t trigger anything unless the text has actually changed
          return state;
        }
        return {
          ...state,
          currentContractViewId: contractViewId,
          stateForCurrentContractViewId: {
            ...stateForCurrentContractViewId,
            titleTextEditsByClauseId: {
              ...stateForCurrentContractViewId.titleTextEditsByClauseId,
              [clauseId]: text,
            },
          },
        };
      }

      case EDIT_BODY_TEXT: {
        const { documentId, clauseId, text } = action.payload;
        if (!documentId || !clauseId) {
          return state;
        }
        const contractViewId = contractViewIdFromDocumentId(documentId);
        const stateForCurrentContractViewId =
          state.currentContractViewId === contractViewId
            ? state.stateForCurrentContractViewId
            : {};
        if (
          stateForCurrentContractViewId.bodyTextEditsByClauseId?.[clauseId] ===
          text
        ) {
          // Don”t trigger anything unless the text has actually changed
          return state;
        }
        return {
          ...state,
          currentContractViewId: contractViewId,
          stateForCurrentContractViewId: {
            ...stateForCurrentContractViewId,
            bodyTextEditsByClauseId: {
              ...stateForCurrentContractViewId.bodyTextEditsByClauseId,
              [clauseId]: text,
            },
          },
        };
      }

      case SET_CURSOR_SELECTION: {
        const { documentId, clauseId, linkToClauseId, selectionOffsetInBody } =
          action.payload;
        if (!documentId || !clauseId) {
          return state;
        }
        const contractViewId = contractViewIdFromDocumentId(documentId);
        const stateForCurrentContractViewId =
          state.currentContractViewId === contractViewId
            ? state.stateForCurrentContractViewId
            : {};
        return {
          ...state,
          currentContractViewId: contractViewId,
          stateForCurrentContractViewId: {
            ...stateForCurrentContractViewId,
            cursorSelection: {
              inClauseId: clauseId,
              linkToClauseId,
              selectionOffsetInBody,
            },
          },
        };
      }

      case CLEAR_CURSOR_SELECTION: {
        const { documentId, clauseId } = action.payload;
        if (!documentId || !clauseId) {
          return state;
        }
        const contractViewId = contractViewIdFromDocumentId(documentId);
        const stateForCurrentContractViewId =
          state.currentContractViewId === contractViewId
            ? state.stateForCurrentContractViewId
            : {};
        if (
          clauseId !==
          stateForCurrentContractViewId?.cursorSelection?.inClauseId
        ) {
          // do nothing if the current selection has been
          // claimed by another clause
          return state;
        }

        return {
          ...state,
          currentContractViewId: contractViewId,
          stateForCurrentContractViewId: {
            ...stateForCurrentContractViewId,
            cursorSelection: {
              inClauseId: null,
              linkToClauseId: null,
              selectionOffsetInBody: undefined,
            },
          },
        };
      }

      case SET_BODY_LINK_HOVER: {
        const {
          documentId,
          clauseId,
          linkToClauseId,
          linkStartOffsetInBody,
          linkEndOffsetInBody,
        } = action.payload;
        if (!documentId || !clauseId) {
          return state;
        }
        const contractViewId = contractViewIdFromDocumentId(documentId);
        const stateForCurrentContractViewId =
          state.currentContractViewId === contractViewId
            ? state.stateForCurrentContractViewId
            : {};
        return {
          ...state,
          currentContractViewId: contractViewId,
          stateForCurrentContractViewId: {
            ...stateForCurrentContractViewId,
            linkHover: {
              inClauseId: clauseId,
              linkToClauseId,
              linkStartOffsetInBody,
              linkEndOffsetInBody,
            },
          },
        };
      }

      case CLEAR_BODY_LINK_HOVER: {
        const { documentId } = action.payload;
        if (!documentId) {
          return state;
        }
        const contractViewId = contractViewIdFromDocumentId(documentId);
        const stateForCurrentContractViewId =
          state.currentContractViewId === contractViewId
            ? state.stateForCurrentContractViewId
            : {};

        return {
          ...state,
          currentContractViewId: contractViewId,
          stateForCurrentContractViewId: {
            ...stateForCurrentContractViewId,
            linkHover: {
              inClauseId: null,
              linkToClauseId: null,
              linkStartOffsetInBody: undefined,
              linkEndOffsetInBody: undefined,
            },
          },
        };
      }

      default:
        return state;
    }
  },
};

//
// Generic reducer for editing document state
//

function editStateReducer(buildEdits, sliceState, action) {
  const { initialDocument, documentId } = action.meta;
  if (!documentId) {
    return sliceState;
  }
  const currentDocumentEdits = sliceState.editStateByDocumentId?.[documentId];

  const rootClauseId =
    currentDocumentEdits?.rootClauseId || initialDocument?.rootClauseId;
  const stateDependentEdits = buildEdits(
    (clauseId, getAllClauseIds) =>
      resolveEditedClause(
        clauseId,
        initialDocument?.clausesById,
        currentDocumentEdits?.clausesById,
        getAllClauseIds,
        rootClauseId,
      ),
    action.payload,
    rootClauseId,
  );
  if (!stateDependentEdits) {
    return sliceState;
  }

  const contractViewId = contractViewIdFromDocumentId(documentId);
  const stateForCurrentContractViewId =
    sliceState.currentContractViewId === contractViewId
      ? sliceState.stateForCurrentContractViewId
      : {};
  const clearTextEdits = Object.entries(stateDependentEdits.edits || {}).reduce(
    (prevClears, [clauseId, clauseEdits]) => ({
      titles: {
        ...prevClears.titles,
        ...(clauseEdits.title !== undefined ? { [clauseId]: null } : {}),
      },
      bodies: {
        ...prevClears.bodies,
        ...(clauseEdits.body !== undefined ? { [clauseId]: null } : {}),
      },
    }),
    { titles: {}, bodies: {} },
  );

  return {
    ...sliceState,
    editStateByDocumentId: {
      ...sliceState.editStateByDocumentId,
      [documentId]: documentEditStateAfterNewEdits(
        currentDocumentEdits,
        stateDependentEdits,
      ),
    },
    currentContractViewId: contractViewId,
    stateForCurrentContractViewId: {
      ...stateForCurrentContractViewId,
      titleTextEditsByClauseId: {
        ...stateForCurrentContractViewId.titleTextEditsByClauseId,
        ...clearTextEdits.titles,
      },
      bodyTextEditsByClauseId: {
        ...stateForCurrentContractViewId.bodyTextEditsByClauseId,
        ...clearTextEdits.bodies,
      },
    },
  };
}

function documentEditStateAfterNewEdits(
  documentEditState = {},
  { edits = {}, forceFocusForContentState, forceFocusAtEnd },
) {
  let newForceFocusState = forceFocusForContentState
    ? {
        forceFocusForContentState,
        forceFocusAtEnd,
      }
    : {};
  const newState = {
    ...documentEditState,
    rootClauseId: edits.rootClauseId || documentEditState.rootClauseId,
    clausesById: Object.entries(edits).reduce(
      (completeEditedClauses, [clauseId, clausePropertyEdits]) => ({
        ...completeEditedClauses,
        [clauseId]: {
          ...documentEditState.clausesById?.[clauseId],
          ...clausePropertyEdits,
        },
      }),
      documentEditState.clausesById || {},
    ),
    ...newForceFocusState,
  };
  return newState;
}

function resolveEditedClause(
  clauseId,
  originalClausesById,
  clauseEditsById,
  getAllClauseIds,
  rootClauseId,
) {
  if (getAllClauseIds) {
    const unionIds = new Set([
      ...Object.keys(originalClausesById || {}),
      ...Object.keys(clauseEditsById || {}),
    ]);
    return unionIds.values();
  }
  const originalClause = originalClausesById?.[clauseId] || {};
  const clauseEdits = clauseEditsById?.[clauseId] || {};
  return {
    showPreamble: false,
    title: null,
    titleHidden: false,
    body: null,
    children: [],
    ...originalClause,
    ...clauseEdits,
    isRoot: rootClauseId ? clauseId === rootClauseId : undefined,
  };
}

//
// Utilities used by actions to transform edit state
//

function findParentClause(ofClauseId, clauseResolver) {
  for (const clauseId of clauseResolver(undefined, true)) {
    const resolvedClause = clauseResolver(clauseId);
    let index = 0;
    for (const childClauseId of resolvedClause.children) {
      if (childClauseId === ofClauseId) {
        return {
          clauseId,
          clause: resolvedClause,
          childIndex: index,
        };
      }
      index += 1;
    }
  }
}

function findClauseLevel(ofClauseId, clauseResolver) {
  let level = 0;
  let parentClause = findParentClause(ofClauseId, clauseResolver);
  while (parentClause) {
    level += 1;
    parentClause = parentClause.clause.isRoot
      ? null
      : findParentClause(parentClause.clauseId, clauseResolver);
  }
  return level;
}

function findMaxLevelOfChildren(fromClauseId, clauseResolver, atLevel) {
  const fromLevel = atLevel ?? findClauseLevel(fromClauseId, clauseResolver);
  let maxLevel = fromLevel;
  const clause = clauseResolver(fromClauseId);
  for (const childClauseId of clause.children) {
    maxLevel = Math.max(
      maxLevel,
      findMaxLevelOfChildren(childClauseId, clauseResolver, fromLevel + 1),
    );
  }
  return maxLevel;
}

function findLastVisibleFromRoot(clauseId, clauseResolver) {
  const clause = clauseResolver(clauseId);
  if (!clause.isCollapsed && clause.children.length) {
    return findLastVisibleFromRoot(
      clause.children[clause.children.length - 1],
      clauseResolver,
    );
  }
  return {
    clauseId,
    clause,
  };
}

function findPreviousVisibleClause(fromClauseId, clauseResolver) {
  const parent = findParentClause(fromClauseId, clauseResolver);
  if (parent) {
    if (parent.childIndex === 0) {
      const grandParent = findParentClause(parent.clauseId, clauseResolver);
      if (
        !grandParent ||
        (grandParent.clause.isRoot && grandParent.clause.children.length <= 1)
      ) {
        return undefined;
      }
      return parent;
    }
    return findLastVisibleFromRoot(
      parent.clause.children[parent.childIndex - 1],
      clauseResolver,
    );
  }
}

function findNextVisibleClause(fromClauseId, clauseResolver) {
  const clause = clauseResolver(fromClauseId);
  if (!clause.isCollapsed && clause.children.length) {
    return {
      clauseId: clause.children[0],
      clause: clauseResolver(clause.children[0]),
    };
  }
  let nextAncestor = findParentClause(fromClauseId, clauseResolver);
  while (nextAncestor) {
    if (nextAncestor.childIndex < nextAncestor.clause.children.length - 1) {
      return {
        clauseId: nextAncestor.clause.children[nextAncestor.childIndex + 1],
        clause: clauseResolver(
          nextAncestor.clause.children[nextAncestor.childIndex + 1],
        ),
      };
    }
    nextAncestor = findParentClause(nextAncestor.clauseId, clauseResolver);
  }
}

function yieldMainThread() {
  // Pause a long-running operation briefly to allow the
  // UI to stay responsive
  return new Promise((resolve) => setTimeout(resolve, 10));
}

//
// Edit state transforms for the actions
//

function editsForPrepareForEditing(
  clauseResolver,
  { initialClauseAtLevel2, ensureInitialEmptyClause = true },
  rootClauseId,
) {
  if (!rootClauseId) {
    // document is invalid, so don’t do anything
    return;
  }
  const rootClause = clauseResolver(rootClauseId);
  const level1Ids = rootClause.children;
  const emptyLevel1ClauseId = uuidv4();
  const emptyLevel2ClauseId = uuidv4();
  if (level1Ids.length === 0) {
    if (initialClauseAtLevel2) {
      if (ensureInitialEmptyClause) {
        return {
          edits: {
            [rootClauseId]: {
              children: [emptyLevel1ClauseId],
            },
            [emptyLevel1ClauseId]: {
              showPreamble: false,
              title: null,
              titleHidden: false,
              body: null,
              children: [emptyLevel2ClauseId],
            },
            [emptyLevel2ClauseId]: {
              title: null,
              titleHidden: false,
              body: null,
              children: [],
            },
          },
        };
      } else {
        return {
          edits: {
            [rootClauseId]: {
              children: [emptyLevel1ClauseId],
            },
            [emptyLevel1ClauseId]: {
              showPreamble: false,
              title: null,
              titleHidden: false,
              body: null,
              children: [],
            },
          },
        };
      }
    } else {
      if (ensureInitialEmptyClause) {
        return {
          edits: {
            [rootClauseId]: {
              children: [emptyLevel1ClauseId],
            },
            [emptyLevel1ClauseId]: {
              title: null,
              titleHidden: false,
              body: null,
              children: [],
            },
          },
        };
      } else {
        return;
      }
    }
  }
  if (
    initialClauseAtLevel2 &&
    level1Ids.length === 1 &&
    ensureInitialEmptyClause
  ) {
    const firstLevel1ClauseId = level1Ids[0];
    const firstLevel1Clause = clauseResolver(firstLevel1ClauseId);
    if (firstLevel1Clause.children.length === 0) {
      return {
        edits: {
          [firstLevel1ClauseId]: {
            children: [emptyLevel2ClauseId],
          },
          [emptyLevel2ClauseId]: {
            title: null,
            titleHidden: false,
            body: null,
            children: [],
          },
        },
      };
    }
  }
}

function editsForShowFirstChapter(clauseResolver, payload, rootClauseId) {
  if (!rootClauseId) {
    // document is invalid, so don’t do anything
    return;
  }
  const rootClause = clauseResolver(rootClauseId);
  const chapterIds = rootClause.children;
  const firstChapterClauseId = chapterIds[0];
  if (!firstChapterClauseId) {
    // no first chapter to show
    return;
  }
  return {
    edits: {
      [firstChapterClauseId]: {
        showPreamble: true,
      },
    },
  };
}

function editsForHideFirstChapter(clauseResolver, payload, rootClauseId) {
  if (!rootClauseId) {
    // document is invalid, so don’t do anything
    return;
  }
  const rootClause = clauseResolver(rootClauseId);
  const chapterIds = rootClause.children;
  const firstChapterClauseId = chapterIds[0];
  if (!firstChapterClauseId) {
    // no first chapter to hide
    return;
  }
  return {
    edits: {
      [firstChapterClauseId]: {
        showPreamble: false,
        title: null,
        body: null,
      },
    },
  };
}

function editsForDivideIntoChapters(clauseResolver, payload, rootClauseId) {
  if (!rootClauseId) {
    // document is invalid, so don’t do anything
    return;
  }
  const rootClause = clauseResolver(rootClauseId);
  const chapterIds = rootClause.children;
  const firstChapterClauseId = chapterIds[0];
  if (chapterIds.length !== 1 || !firstChapterClauseId) {
    // not valid unless there’s currently only one chapter
    return;
  }
  const secondChapterClauseId = uuidv4();
  const emptyLevel2ClauseId = uuidv4();
  return {
    edits: {
      [firstChapterClauseId]: {
        showPreamble: true,
      },
      [rootClauseId]: {
        children: [...chapterIds, secondChapterClauseId],
      },
      [secondChapterClauseId]: {
        showPreamble: true,
        title: null,
        titleHidden: false,
        body: null,
        children: [emptyLevel2ClauseId],
      },
      [emptyLevel2ClauseId]: {
        title: null,
        titleHidden: false,
        body: null,
        children: [],
      },
    },
  };
}

function editsForAddChapterBefore(clauseResolver, { chapterId }) {
  const parent = findParentClause(chapterId, clauseResolver);
  if (!parent || !parent.clause.isRoot) {
    // Not a valid chapter
    return;
  }
  const newChapterClauseId = uuidv4();
  const emptyLevel2ClauseId = uuidv4();
  return {
    edits: {
      [parent.clauseId]: {
        children: [
          ...parent.clause.children.slice(0, parent.childIndex),
          newChapterClauseId,
          ...parent.clause.children.slice(parent.childIndex),
        ],
      },
      [newChapterClauseId]: {
        showPreamble: true,
        title: null,
        titleHidden: false,
        body: null,
        children: [emptyLevel2ClauseId],
      },
      [emptyLevel2ClauseId]: {
        title: null,
        titleHidden: false,
        body: null,
        children: [],
      },
    },
  };
}

function editsForAddChapterAfter(clauseResolver, { chapterId }) {
  const parent = findParentClause(chapterId, clauseResolver);
  if (!parent || !parent.clause.isRoot) {
    // Not a valid chapter
    return;
  }
  const newChapterClauseId = uuidv4();
  const emptyLevel2ClauseId = uuidv4();
  return {
    edits: {
      [parent.clauseId]: {
        children: [
          ...parent.clause.children.slice(0, parent.childIndex + 1),
          newChapterClauseId,
          ...parent.clause.children.slice(parent.childIndex + 1),
        ],
      },
      [newChapterClauseId]: {
        showPreamble: true,
        title: null,
        titleHidden: false,
        body: null,
        children: [emptyLevel2ClauseId],
      },
      [emptyLevel2ClauseId]: {
        title: null,
        titleHidden: false,
        body: null,
        children: [],
      },
    },
  };
}

function editsForUpdateClause(clauseResolver, { clauseId, edits }) {
  return {
    edits: {
      [clauseId]: edits,
    },
  };
}

function editsForUpdateClauses(clauseResolver, { edits }) {
  return {
    edits,
  };
}

function editsForMoveCursorFromClause(
  clauseResolver,
  { fromClauseId, upOrDown, titleOrBody, startOrEnd, clauseEdits },
) {
  const toClause =
    upOrDown === 'up'
      ? findPreviousVisibleClause(fromClauseId, clauseResolver)
      : findNextVisibleClause(fromClauseId, clauseResolver);
  if (toClause) {
    if (titleOrBody === 'title' && toClause.clause.titleHidden) {
      titleOrBody = 'body';
    }
    const forceFocusForContentState =
      titleOrBody === 'title'
        ? toClause.clause.title || {
            blocks: [{ text: '' }],
            entityMap: {},
          }
        : toClause.clause.body || {
            blocks: [{ text: '' }],
            entityMap: {},
          };
    const edits =
      titleOrBody === 'title'
        ? {
            [fromClauseId]: clauseEdits || {},
            [toClause.clauseId]: {
              title: forceFocusForContentState,
              // force the clause component to be recreated, so that
              // the selection can be forced
              forceClearComponentState:
                (toClause.clause.forceClearComponentState || 0) + 1,
            },
          }
        : {
            [fromClauseId]: clauseEdits || {},
            [toClause.clauseId]: {
              body: forceFocusForContentState,
              // force the clause component to be recreated, so that
              // the selection can be forced
              forceClearComponentState:
                (toClause.clause.forceClearComponentState || 0) + 1,
            },
          };
    return {
      edits,
      forceFocusForContentState,
      forceFocusAtEnd: startOrEnd === 'end',
    };
  }
}

function editsForInsertClausesAfter(
  clauseResolver,
  {
    insertAfterClauseId,
    clauseEdits,
    newClauses,
    forceFocusForContentState,
    forceFocusAtEnd,
    preventChildInsertion,
  },
) {
  const newClauseIds = newClauses.map(() => uuidv4());
  const clause = clauseResolver(insertAfterClauseId);
  if (
    !preventChildInsertion &&
    clause &&
    clause.children.length &&
    !clause.isCollapsed
  ) {
    // if the clause has visible children, then insert above
    // the children, rather than at the same level
    return {
      edits: newClauseIds.reduce(
        (edits, newClauseId, newClauseIndex) => ({
          ...edits,
          [newClauseId]: newClauses[newClauseIndex],
        }),
        {
          [insertAfterClauseId]: {
            ...clauseEdits,
            children: [...newClauseIds, ...clause.children],
            // force the clause component to be recreated, to
            // avoid issues with entities being cleared
            forceClearComponentState:
              (clause.forceClearComponentState || 0) + 1,
          },
        },
      ),
      forceFocusForContentState,
      forceFocusAtEnd,
    };
  }
  const parent = findParentClause(insertAfterClauseId, clauseResolver);
  if (parent) {
    return {
      edits: newClauseIds.reduce(
        (edits, newClauseId, newClauseIndex) => ({
          ...edits,
          [newClauseId]: newClauses[newClauseIndex],
        }),
        {
          [insertAfterClauseId]: {
            ...clauseEdits,
            // force the clause component to be recreated, to
            // avoid issues with entities being cleared
            forceClearComponentState:
              (clause.forceClearComponentState || 0) + 1,
          },
          [parent.clauseId]: {
            children: [
              ...parent.clause.children.slice(0, parent.childIndex + 1),
              ...newClauseIds,
              ...parent.clause.children.slice(parent.childIndex + 1),
            ],
          },
        },
      ),
      forceFocusForContentState,
      forceFocusAtEnd,
    };
  }
}

function editsForIndentClauseRight(
  clauseResolver,
  {
    indentClauseId,
    maxIndentLevel,
    clauseEdits,
    forceFocusForContentState,
    forceFocusAtEnd,
  },
) {
  const parent = findParentClause(indentClauseId, clauseResolver);
  if (
    parent &&
    parent.childIndex > 0 &&
    (maxIndentLevel === undefined ||
      findMaxLevelOfChildren(indentClauseId, clauseResolver) < maxIndentLevel)
  ) {
    const clauseAboveId = parent.clause.children[parent.childIndex - 1];
    const clauseAbove = clauseResolver(clauseAboveId);
    return {
      edits: {
        // Apply pending edits to the clause being indented
        [indentClauseId]: clauseEdits,
        // remove from parent
        [parent.clauseId]: {
          children: parent.clause.children.filter(
            (siblingClauseId) => siblingClauseId !== indentClauseId,
          ),
        },
        // add as child of clause above
        [clauseAboveId]: {
          children: [...clauseAbove.children, indentClauseId],
        },
      },
      forceFocusForContentState,
      forceFocusAtEnd,
    };
  }
}

function editsForIndentClauseLeft(
  clauseResolver,
  { indentClauseId, clauseEdits, forceFocusForContentState, forceFocusAtEnd },
) {
  const clause = clauseResolver(indentClauseId);
  const parent = findParentClause(indentClauseId, clauseResolver);
  const grandparent = findParentClause(parent.clauseId, clauseResolver);
  if (clause && parent && grandparent) {
    if (!clause.children.length) {
      // As a special convenience, if the outdented clause has no
      // children, then assume that the user wants to split the
      // siblings and “break the clause out” to the parent level
      return {
        edits: {
          // remove from parent: the outdented clause,
          // and any clauses below it
          [parent.clauseId]: {
            children: parent.clause.children.slice(0, parent.childIndex),
          },
          // insert the outdented clause into its parent after its grandparent
          [grandparent.clauseId]: {
            children: [
              ...grandparent.clause.children.slice(
                0,
                grandparent.childIndex + 1,
              ),
              indentClauseId,
              ...grandparent.clause.children.slice(grandparent.childIndex + 1),
            ],
          },
          [indentClauseId]: {
            // Apply pending edits to the clause being outdented
            ...clauseEdits,
            // add the clauses that were below as children
            children: parent.clause.children.slice(parent.childIndex + 1),
          },
        },
        forceFocusForContentState,
        forceFocusAtEnd,
      };
    }
    return {
      edits: {
        // remove from parent
        [parent.clauseId]: {
          children: parent.clause.children.filter(
            (siblingClauseId) => siblingClauseId !== indentClauseId,
          ),
        },
        // Apply pending edits to the clause being indented
        [indentClauseId]: clauseEdits,
        // insert after grandparent
        [grandparent.clauseId]: {
          children: [
            ...grandparent.clause.children.slice(0, grandparent.childIndex + 1),
            indentClauseId,
            ...grandparent.clause.children.slice(grandparent.childIndex + 1),
          ],
        },
      },
      forceFocusForContentState,
      forceFocusAtEnd,
    };
  }
}

function editsForDeleteClause(
  clauseResolver,
  { deleteClauseId, moveCursorUp },
) {
  const parent = findParentClause(deleteClauseId, clauseResolver);
  if (parent) {
    if (moveCursorUp) {
      const previousClause = findPreviousVisibleClause(
        deleteClauseId,
        clauseResolver,
      );
      let edits = {};
      let previousClauseBody;
      if (previousClause) {
        previousClauseBody = previousClause.clause.body || {
          blocks: [{ text: '' }],
          entityMap: {},
        };
        edits = {
          // force the clause component to be recreated, so that
          // the selection can be forced
          [previousClause.clauseId]: {
            body: previousClauseBody,
            forceClearComponentState:
              (previousClause.clause.forceClearComponentState || 0) + 1,
          },
        };
      }
      edits = {
        ...edits,
        // remove from parent
        [parent.clauseId]: {
          ...edits[parent.clauseId],
          children: parent.clause.children.filter(
            (siblingClauseId) => siblingClauseId !== deleteClauseId,
          ),
        },
      };
      return {
        edits,
        forceFocusForContentState: previousClauseBody,
        forceFocusAtEnd: true,
      };
    } else {
      return {
        edits: {
          // remove from parent
          [parent.clauseId]: {
            children: parent.clause.children.filter(
              (siblingClauseId) => siblingClauseId !== deleteClauseId,
            ),
          },
        },
      };
    }
  }
}

//
// Action creator utilities
//

function resolveDocumentForSave(
  { rootClauseId, clausesById = {} },
  originalClausesById,
  clauseEditsById,
) {
  const resolvedClause = resolveEditedClause(
    rootClauseId,
    originalClausesById,
    clauseEditsById,
  );
  const children = resolvedClause.children
    .map((childClauseId) => {
      const transformedChild = resolveDocumentForSave(
        { rootClauseId: childClauseId, clausesById },
        originalClausesById,
        clauseEditsById,
      );
      clausesById = transformedChild.clausesById;
      return transformedChild.rootClauseId;
    })
    .filter((childClauseId) => !!childClauseId);
  clausesById = {
    ...clausesById,
    [rootClauseId]: {
      ...resolvedClause,
      children,
    },
  };
  return {
    rootClauseId,
    clausesById,
  };
}

function makeEditDocumentStateActionCreator(applyEditsActionCreator) {
  return function (actionCreatorArgs) {
    // This is the action creator, called from the Outliner code
    const { initialDocumentSelector, saveDocument, documentId } =
      actionCreatorArgs;

    return async (dispatch, getState) => {
      // This is the thunk, with access to the whole state
      // First, apply the edit and update the Outliner’s edit state
      dispatch(
        applyEditsActionCreator({
          ...actionCreatorArgs,
          initialDocument: initialDocumentSelector(getState(), documentId),
        }),
      );
      // Then calculate the resolved document and dispatch
      // the action we’ve been given to save it (presumably to
      // the server, but doesn’t have to be)
      const initialDocument = initialDocumentSelector(getState(), documentId);
      const newDocumentEdits = this.selectEditState(getState(), documentId);
      const updatedDocumentForSave = resolveDocumentForSave(
        {
          rootClauseId:
            newDocumentEdits?.rootClauseId || initialDocument?.rootClauseId,
          clausesById: {},
        },
        initialDocument?.clausesById,
        newDocumentEdits?.clausesById,
      );
      await saveDocument({
        documentId,
        updatedDocumentForSave,
      });
    };
  };
}

function findAllCollapsibleClauseIdsFromRoot(
  clauseId,
  originalClausesById,
  clauseEditsById,
) {
  const clause = resolveEditedClause(
    clauseId,
    originalClausesById,
    clauseEditsById,
  );
  if (!clause.children.length) {
    // clauses without children aren’t collapsible
    return [];
  }
  return clause.children.reduce(
    (prevClauseIds, childClauseId) => [
      ...prevClauseIds,
      ...findAllCollapsibleClauseIdsFromRoot(
        childClauseId,
        originalClausesById,
        clauseEditsById,
      ),
    ],
    // Root clause is not collapsible
    clause.isRoot ? [] : [clauseId],
  );
}

class ContractProse extends Service {
  makeContractViewId = (profileId, contractId) =>
    `${contractId || ''}:${profileId || ''}`;

  makeDocumentIdForClauses = (profileId, contractId) =>
    documentIdFromContractViewIdForClauses(
      this.makeContractViewId(profileId, contractId),
    );

  makeDocumentIdForDefinitions = (profileId, contractId) =>
    documentIdFromContractViewIdForDefinitions(
      this.makeContractViewId(profileId, contractId),
    );

  makeInitialDocumentSelectorForClauses = (
    profileId,
    contractId,
    contract,
    allowEditing,
  ) => {
    return () =>
      this.selectContractClauses(
        contract,
        this.makeContractViewId(profileId, contractId),
        allowEditing,
      );
  };

  makeInitialDocumentSelectorForDefinitions = (
    profileId,
    contractId,
    contract,
    allowEditing,
  ) => {
    return () =>
      this.selectContractDefinitions(
        contract,
        this.makeContractViewId(profileId, contractId),
        allowEditing,
      );
  };

  //
  // State and selectors
  //

  // Contract Body (as posted):
  selectContractSerializedClausesAsPosted = (contract) =>
    contract?.bodyOfAgreement?.details?.chapters;
  selectContractSerializedDefinitionsAsPosted = (contract) =>
    contract?.bodyOfAgreement?.details?.definitions;
  selectContractClausesAsPosted = createCachedSelector(
    this.selectContractSerializedClausesAsPosted,
    rehydrateContractClauses,
  )({
    keySelector: (contract, contractViewId) => contractViewId,
    cacheObject: new FlatMapCache(),
  });
  selectContractDefinitionsAsPosted = createCachedSelector(
    this.selectContractSerializedDefinitionsAsPosted,
    rehydrateContractDefinitions,
  )({
    keySelector: (contract, contractViewId) => contractViewId,
    cacheObject: new FlatMapCache(),
  });

  // Contract Body (for editing):
  selectContractSerializedClausesForEditing = (contract) =>
    contract?.draft?.bodyOfAgreement?.details?.chapters;
  selectContractSerializedDefinitionsForEditing = (contract) =>
    contract?.draft?.bodyOfAgreement?.details?.definitions;
  selectContractClausesForEditing = createCachedSelector(
    this.selectContractSerializedClausesForEditing,
    rehydrateContractClauses,
  )({
    keySelector: (contract, contractViewId) => contractViewId,
    cacheObject: new FlatMapCache(),
  });
  selectContractDefinitionsForEditing = createCachedSelector(
    this.selectContractSerializedDefinitionsForEditing,
    rehydrateContractDefinitions,
  )({
    keySelector: (contract, contractViewId) => contractViewId,
    cacheObject: new FlatMapCache(),
  });

  selectContractClauses = (contract, contractViewId, forEditing) =>
    forEditing
      ? this.selectContractClausesForEditing(contract, contractViewId)
      : this.selectContractClausesAsPosted(contract, contractViewId);
  selectContractDefinitions = (contract, contractViewId, forEditing) =>
    forEditing
      ? this.selectContractDefinitionsForEditing(contract, contractViewId)
      : this.selectContractDefinitionsAsPosted(contract, contractViewId);

  //
  // Definition search Selectors
  //

  // Derived state 1: List of defined terms, sorted and indexed
  selectAllDefinitionTermsUnsorted = (
    state,
    contract,
    contractViewId,
    forEditing,
  ) => {
    const documentId =
      documentIdFromContractViewIdForDefinitions(contractViewId);
    const initialDocumentSelector = () =>
      this.selectContractDefinitions(contract, contractViewId, forEditing);
    const rootClauseId = this.selectRootClauseId(
      state,
      documentId,
      initialDocumentSelector,
    );
    const level1ClauseIds = this.selectClauseChildren(
      state,
      documentId,
      rootClauseId,
      initialDocumentSelector,
    );
    // No effort here to sort or deduplicate the list, because
    // this selector is used as a frequent check for changes, so should
    // do as little work as possible. Letting some non-changes
    // through is OK.
    return level1ClauseIds.map((clauseId) => ({
      clauseId,
      term: this.selectClauseEditedTitleText(
        state,
        documentId,
        clauseId,
        initialDocumentSelector,
      ),
    }));
  };
  // Equality test for the results of the function above
  testAllDefinitionsEqual = (a, b) => {
    if (a.length !== b.length) {
      return false;
    }
    return a.every(
      (aTerm, index) =>
        aTerm.term === b[index].term && aTerm.clauseId === b[index].clauseId,
    );
  };
  createDefinitionsIndex = (unsortedDefinitionsList) =>
    unsortedDefinitionsList
      .map((definition) => {
        let index;
        if (definition.term) {
          index = wordIndexFromText(definition.term, {
            clauseId: definition.clauseId,
          });
          // remove leading and trailing whitespace
          index = index.slice(
            index[0]?.matchText === ' ' ? 1 : 0,
            index.length > 1 && index[index.length - 1]?.matchText === ' '
              ? -1
              : undefined,
          );
          if (index?.length) {
            // insert length for sorting
            return {
              termLength: index.reduce(
                (totalLength, segment) =>
                  totalLength + segment.matchText.length,
                0,
              ),
              index,
            };
          }
        }
        return null;
      })
      // remove empty terms
      .filter((definition) => definition)
      // sort from longest to shortest term
      .sort((a, b) => b.termLength - a.termLength)
      // remove lengths from result
      .map((definition) => definition.index);
  // Customize reselect to produce selectors that test for
  // simple equality in the list of terms
  createDefinitionTermsSelector = createSelectorCreator(
    defaultMemoize,
    this.testAllDefinitionsEqual,
  );
  currentSelectDefinitionsIndexedForSearch = null;
  contractViewIdForCurrentSelectDefinitionsIndexedForSearch = null;
  selectDefinitionsIndexedForSearch = (
    state,
    contract,
    contractViewId,
    forEditing,
  ) => {
    if (
      contractViewId !==
      this.contractViewIdForCurrentSelectDefinitionsIndexedForSearch
    ) {
      // This implements a two-level cache
      // L1 (this function):
      //   key = contractViewId, size = 1
      //   (so reset for a new contract)
      // L2 (createSelector)
      //   key = unsorted definition terms, size = 1
      //   (if the definitions haven’t changed, return cached index)
      this.currentSelectDefinitionsIndexedForSearch =
        this.createDefinitionTermsSelector(
          this.selectAllDefinitionTermsUnsorted,
          this.createDefinitionsIndex,
        );
      this.contractViewIdForCurrentSelectDefinitionsIndexedForSearch =
        contractViewId;
    }
    return this.currentSelectDefinitionsIndexedForSearch(
      state,
      contract,
      contractViewId,
      forEditing,
    );
  };
  selectNumberOfDefinedTerms = (state, contract, contractViewId, forEditing) =>
    this.selectDefinitionsIndexedForSearch(
      state,
      contract,
      contractViewId,
      forEditing,
    ).length;

  // Derived state 2: The body text of each clause, indexed
  currentSelectWordIndexForClauseBody = null;
  contractViewIdForCurrentSelectWordIndexForClauseBody = null;
  selectWordIndexForClauseBody = (
    state,
    documentId,
    clauseId,
    initialDocumentSelector,
  ) => {
    const contractViewId = contractViewIdFromDocumentId(documentId);
    if (
      contractViewId !==
      this.contractViewIdForCurrentSelectWordIndexForClauseBody
    ) {
      // This implements a three-level chache
      // L1 (this function):
      //   key = contractViewId, size = 1
      //   (so reset whole cache for a new contract)
      // L2 (createCachedSelector):
      //   key = clauseId, unlimited size
      //   (cache one word index per clause)
      // L3 (createSelector internally)
      //   key = edited body text, size = 1
      //   (if the text is the same, return cached index)
      this.currentSelectWordIndexForClauseBody = createCachedSelector(
        this.selectClauseEditedBodyText,
        wordIndexFromText,
      )({
        keySelector: (
          state,
          documentId,
          clauseId,
          // initialDocumentSelector
        ) => clauseId,
        cacheObject: new FlatMapCache(),
      });
      this.contractViewIdForCurrentSelectWordIndexForClauseBody =
        contractViewId;
    }
    return this.currentSelectWordIndexForClauseBody(
      state,
      documentId,
      clauseId,
      initialDocumentSelector,
    );
  };

  // Derived state 3: Definition term matches for each clause
  currentSelectDefinitionLinksForClause = null;
  contractViewIdForCurrentSelectDefinitionLinksForClause = null;
  selectDefinitionLinksForClause = (
    state,
    contract,
    documentId,
    clauseId,
    initialDocumentSelector,
    forEditing,
  ) => {
    const contractViewId = contractViewIdFromDocumentId(documentId);
    if (
      contractViewId !==
      this.contractViewIdForCurrentSelectDefinitionLinksForClause
    ) {
      // This implements a three-level chache
      // L1 (this function):
      //   key = contractViewId, size = 1
      //   (so reset whole cache for a new contract)
      // L2 (createCachedSelector):
      //   key = clauseId, unlimited size
      //   (cache one word index per clause)
      // L3 (createSelector internally)
      //   keys = terms, size = 1
      //   (if the text is the same, return cached index)
      this.currentSelectDefinitionLinksForClause = createCachedSelector(
        (
          state,
          contract,
          documentId,
          clauseId,
          initialDocumentSelector,
          forEditing,
        ) =>
          this.selectDefinitionsIndexedForSearch(
            state,
            contract,
            contractViewId,
            forEditing,
          ),
        (state, contract, documentId, clauseId, initialDocumentSelector) =>
          this.selectWordIndexForClauseBody(
            state,
            documentId,
            clauseId,
            initialDocumentSelector,
          ),
        searchForDefinitionTermMatches,
      )({
        keySelector: (
          state,
          contract,
          documentId,
          clauseId,
          // initialDocumentSelector,
          // forEditing,
        ) => clauseId,
        cacheObject: new FlatMapCache(),
      });
      this.contractViewIdForCurrentSelectDefinitionLinksForClause =
        contractViewId;
    }
    // Format of results:
    // {
    //   searchedText: "Some text containing the term 'defined term'",
    //   matches: [
    //     {
    //       searchedText: "Some text containing the term 'defined term'",
    //       searchedTextMatchOffset: 31,
    //       searchedTextMatchLength: 12,
    //       searchedSegments: [...], // from selectWordIndexForClauseBody()
    //       searchedSegmentOffset: 10,
    //       termSegments: [         // from selectDefinitionsIndexedForSearch
    //         {
    //           segment: "Defined",
    //           matchText: "Defined",
    //           index: 1,
    //           input: " Defined  Term ",
    //           isWordLike: true,
    //           clauseId: "<Definition ID>",
    //         }
    //         {
    //           segment: "  ",
    //           matchText: " ",
    //           index: 8,
    //           input: " Defined  Term ",
    //           isWordLike: false,
    //           clauseId: "<Definition ID>",
    //         }
    //         {
    //           segment: "Term",
    //           matchText: "Term",
    //           index: 10,
    //           input: " Defined  Term ",
    //           isWordLike: true,
    //           clauseId: "<Definition ID>",
    //         }
    //       ]
    //     }
    //   ],
    //   matchesByClauseId: Map({
    //     "<Definition ID> ": [...just the matches for <Definition ID>],
    //     ...
    //   })
    // }
    return this.currentSelectDefinitionLinksForClause(
      state,
      contract,
      documentId,
      clauseId,
      initialDocumentSelector,
      forEditing,
    );
  };

  // Derived state 4: Definition links in body text
  currentSelectAllDefinitionLinks = null;
  contractViewIdForCurrentSelectAllDefinitionLinks = null;
  createDefinitionLinksForCurrentSelectAllDefinitionLinks = null;
  selectAllDefinitionLinks = (
    state,
    contract,
    contractViewId,
    forEditing,
    createDefinitionLinks,
  ) => {
    if (
      contractViewId !==
        this.contractViewIdForCurrentSelectAllDefinitionLinks ||
      createDefinitionLinks !==
        this.createDefinitionLinksForCurrentSelectAllDefinitionLinks
    ) {
      // This implements a two-level cache
      // L1 (this function):
      //   key = contractViewId + createDefinitionLinks, size = 1
      //   (so reset for a new contract)
      // L2 (createSelector)
      //   key = unsorted definition terms, size = 1
      //   (if the definitions haven’t changed, return cached links)
      this.currentSelectAllDefinitionLinks = this.createDefinitionTermsSelector(
        this.selectAllDefinitionTermsUnsorted,
        createDefinitionLinks,
      );
      this.contractViewIdForCurrentSelectAllDefinitionLinks = contractViewId;
      this.createDefinitionLinksForCurrentSelectAllDefinitionLinks =
        createDefinitionLinks;
    }
    return this.currentSelectAllDefinitionLinks(
      state,
      contract,
      contractViewId,
      forEditing,
    );
  };

  // Derived state 5: All uses of one term
  iterateOverUsesOfDefinedTerm = function* (
    state,
    contract,
    definitionClauseId,
    contractViewId,
    forEditing,
  ) {
    const bodyDocumentId =
      documentIdFromContractViewIdForClauses(contractViewId);
    const definitionsDocumentId =
      documentIdFromContractViewIdForDefinitions(contractViewId);
    const bodyInitialDocumentSelector = () =>
      this.selectContractClauses(contract, contractViewId, forEditing);
    const definitionsInitialDocumentSelector = () =>
      this.selectContractDefinitions(contract, contractViewId, forEditing);

    const bodyClauseIds = this.selectAllClausesForDocument(
      state,
      bodyDocumentId,
      bodyInitialDocumentSelector,
    );
    for (const { clauseId, level } of bodyClauseIds) {
      if (level > 0) {
        const matches = this.selectDefinitionLinksForClause(
          state,
          contract,
          bodyDocumentId,
          clauseId,
          bodyInitialDocumentSelector,
          forEditing,
        ).matchesByClauseId.get(definitionClauseId);
        if (matches?.length) {
          yield {
            documentId: bodyDocumentId,
            clauseId,
            matches,
          };
        }
      }
    }

    const definitionClauseIds = this.selectAllClausesForDocument(
      state,
      definitionsDocumentId,
      definitionsInitialDocumentSelector,
    );
    for (const { clauseId, level } of definitionClauseIds) {
      if (level > 0) {
        const matches = this.selectDefinitionLinksForClause(
          state,
          contract,
          definitionsDocumentId,
          clauseId,
          definitionsInitialDocumentSelector,
          forEditing,
        ).matchesByClauseId.get(definitionClauseId);
        if (matches?.length) {
          yield {
            documentId: definitionsDocumentId,
            clauseId,
            matches,
          };
        }
      }
    }
  };
  selectNumberOfUsesOfDefinedTerm = (
    state,
    contract,
    definitionClauseId,
    contractViewId,
    forEditing,
  ) => {
    let totalUses = 0;
    for (const usesInClause of this.iterateOverUsesOfDefinedTerm(
      state,
      contract,
      definitionClauseId,
      contractViewId,
      forEditing,
    )) {
      totalUses += usesInClause.matches.length;
    }
    return totalUses;
  };
  selectIndexOfUseOfDefinedTerm = (
    state,
    contract,
    definitionClauseId,
    documentId,
    clauseId,
    textOffset,
    forEditing,
  ) => {
    const contractViewId = contractViewIdFromDocumentId(documentId);
    let totalUses = 0;
    for (const usesInClause of this.iterateOverUsesOfDefinedTerm(
      state,
      contract,
      definitionClauseId,
      contractViewId,
      forEditing,
    )) {
      if (
        usesInClause.documentId === documentId &&
        usesInClause.clauseId === clauseId
      ) {
        let overallIndex = totalUses + 1;
        for (const match of usesInClause.matches) {
          if (
            textOffset >= match.searchedTextMatchOffset &&
            textOffset <=
              match.searchedTextMatchOffset + match.searchedTextMatchLength
          ) {
            return overallIndex;
          }
          overallIndex += 1;
        }
        return undefined;
      }
      totalUses += usesInClause.matches.length;
    }
    return undefined;
  };

  previousUnusedDefinitionsResult = null;
  selectAllUnusedDefinitions = (
    state,
    contract,
    contractViewId,
    forEditing,
  ) => {
    const definitionsDocumentId =
      documentIdFromContractViewIdForDefinitions(contractViewId);
    const definitionsInitialDocumentSelector = () =>
      this.selectContractDefinitions(contract, contractViewId, forEditing);
    const definitionClauseIds = this.selectAllClausesForDocument(
      state,
      definitionsDocumentId,
      definitionsInitialDocumentSelector,
    );
    const allUnusedDefinitions = [];
    for (const { clauseId: definitionClauseId, level } of definitionClauseIds) {
      if (level > 0) {
        let foundSomeUses = false;
        for (const usesInClause of this.iterateOverUsesOfDefinedTerm(
          state,
          contract,
          definitionClauseId,
          contractViewId,
          forEditing,
        )) {
          if (usesInClause.matches.length) {
            foundSomeUses = true;
            break;
          }
        }
        if (!foundSomeUses) {
          allUnusedDefinitions.push(definitionClauseId);
        }
      }
    }

    // As a simple optimization to avoid unnecessary work in chained selectors,
    // we compare with the last value returned, and use that instead if it’s the
    // same. This is worth it because although this selector is (fairly) cheap,
    // it would otherwise return a new object on every call, and we only call
    // this in one place.
    if (deepEqual(this.previousUnusedDefinitionsResult, allUnusedDefinitions)) {
      return this.previousUnusedDefinitionsResult;
    }
    this.previousUnusedDefinitionsResult = allUnusedDefinitions;
    return allUnusedDefinitions;
  };

  //
  // Outliner edit state
  //

  selectClauseIsCollapsed = (state, documentId, clauseId) =>
    !!state[SLICE]?.localEditorStateByDocumentId?.[documentId]?.[clauseId]
      ?.isCollapsed;

  selectEditState = (state, documentId) =>
    state[SLICE]?.editStateByDocumentId?.[documentId];
  selectInitialDocumentClausesById = (
    state,
    documentId,
    initialDocumentSelector,
  ) => initialDocumentSelector(state, documentId)?.clausesById;
  selectEditedDocumentClausesById = (state, documentId) =>
    this.selectEditState(state, documentId)?.clausesById;

  selectStateForDocument = (state, documentId) =>
    state[SLICE]?.currentContractViewId ===
    contractViewIdFromDocumentId(documentId)
      ? state[SLICE]?.stateForCurrentContractViewId
      : {};
  selectTitleTextEditsByClauseId = (state, documentId) =>
    this.selectStateForDocument(state, documentId)?.titleTextEditsByClauseId;
  selectTitleTextEditsForClause = (state, documentId, clauseId) =>
    this.selectStateForDocument(state, documentId)?.titleTextEditsByClauseId?.[
      clauseId
    ];
  selectBodyTextEditsByClauseId = (state, documentId) =>
    this.selectStateForDocument(state, documentId)?.bodyTextEditsByClauseId;
  selectBodyTextEditsForClause = (state, documentId, clauseId) =>
    this.selectStateForDocument(state, documentId)?.bodyTextEditsByClauseId?.[
      clauseId
    ];
  selectLinkIsHighlighted = (state, documentId, toClauseId) => {
    const cursorSelection = this.selectStateForDocument(
      state,
      documentId,
    )?.cursorSelection;
    if (cursorSelection?.linkToClauseId) {
      // If the cursor is in any use of the link, then highlight
      return cursorSelection.linkToClauseId === toClauseId;
    } else if (cursorSelection?.inClauseId) {
      // If the cursor is inside the linked clause itself
      // then highlight, unless the cursor is within another link
      return cursorSelection.inClauseId === toClauseId;
    }
    return false;
  };
  selectLinkIsSelected = (
    state,
    documentId,
    toClauseId,
    fromClauseId,
    linkTextStart,
    linkTextEnd,
  ) => {
    const cursorSelection = this.selectStateForDocument(
      state,
      documentId,
    )?.cursorSelection;
    return (
      cursorSelection?.inClauseId === fromClauseId &&
      cursorSelection?.linkToClauseId === toClauseId &&
      cursorSelection?.selectionOffsetInBody >= linkTextStart &&
      cursorSelection?.selectionOffsetInBody <= linkTextEnd
    );
  };
  selectLinkIdToShowInPopup = (state, documentId, fromClauseId) => {
    const cursorSelection = this.selectStateForDocument(
      state,
      documentId,
    )?.cursorSelection;
    if (
      cursorSelection?.inClauseId === fromClauseId &&
      cursorSelection?.linkToClauseId &&
      cursorSelection?.linkToClauseId !== fromClauseId
    ) {
      return cursorSelection.linkToClauseId;
    }
    return null;
  };
  selectLinkedOffsetInClauseBody = (state, documentId, clauseId) => {
    const cursorSelection = this.selectStateForDocument(
      state,
      documentId,
    )?.cursorSelection;
    if (cursorSelection?.inClauseId === clauseId) {
      return cursorSelection.selectionOffsetInBody;
    }
    return undefined;
  };
  selectHoveredLinkOffsetInClauseBody = (state, documentId, clauseId) => {
    const linkHover = this.selectStateForDocument(state, documentId)?.linkHover;
    if (
      linkHover?.inClauseId === clauseId &&
      linkHover?.linkStartOffsetInBody !== undefined &&
      linkHover?.linkEndOffsetInBody !== undefined
    ) {
      return Math.floor(
        (linkHover.linkStartOffsetInBody + linkHover.linkEndOffsetInBody) * 0.5,
      );
    }
    return undefined;
  };

  selectRootClauseId = (state, documentId, initialDocumentSelector) =>
    this.selectEditState(state, documentId)?.rootClauseId ||
    initialDocumentSelector(state, documentId)?.rootClauseId;

  // Returns a new object every time, so must be used with a shallowEqual
  // equality test to avoid rendering on every store update
  selectClause = (state, documentId, clauseId, initialDocumentSelector) =>
    resolveEditedClause(
      clauseId,
      this.selectInitialDocumentClausesById(
        state,
        documentId,
        initialDocumentSelector,
      ),
      this.selectEditedDocumentClausesById(state, documentId),
    );

  selectClauseTitle = (state, documentId, clauseId, initialDocumentSelector) =>
    this.selectClause(state, documentId, clauseId, initialDocumentSelector)
      ?.title;

  selectClauseTitleHidden = (
    state,
    documentId,
    clauseId,
    initialDocumentSelector,
  ) =>
    this.selectClause(state, documentId, clauseId, initialDocumentSelector)
      ?.titleHidden;

  selectClauseBody = (state, documentId, clauseId, initialDocumentSelector) =>
    this.selectClause(state, documentId, clauseId, initialDocumentSelector)
      ?.body;

  selectClauseBodyMarkdown = (
    state,
    documentId,
    clauseId,
    initialDocumentSelector,
  ) =>
    this.selectClauseBody(state, documentId, clauseId, initialDocumentSelector)
      ?.text;

  currentSelectClauseBodyAsDraftRaw = null;
  contractViewIdForCurrentSelectClauseBodyAsDraftRaw = null;
  selectClauseBodyAsDraftRaw = (
    state,
    documentId,
    clauseId,
    initialDocumentSelector,
  ) => {
    const contractViewId = contractViewIdFromDocumentId(documentId);
    if (
      contractViewId !== this.contractViewIdForCurrentSelectClauseBodyAsDraftRaw
    ) {
      // This implements a three-level chache
      // L1 (this function):
      //   key = contractViewId, size = 1
      //   (so reset whole cache for a new contract)
      // L2 (createCachedSelector):
      //   key = clauseId, unlimited size
      //   (cache one markdown parse per clause)
      // L3 (createSelector internally)
      //   key = markdown text, size = 1
      //   (if the text is the same, return cache)
      this.currentSelectClauseBodyAsDraftRaw = createCachedSelector(
        this.selectClauseBodyMarkdown,
        markdownToDraftRaw,
      )({
        keySelector: (
          state,
          documentId,
          clauseId,
          // initialDocumentSelector
        ) => clauseId,
        cacheObject: new FlatMapCache(),
      });
      this.contractViewIdForCurrentSelectClauseBodyAsDraftRaw = contractViewId;
    }
    return this.currentSelectClauseBodyAsDraftRaw(
      state,
      documentId,
      clauseId,
      initialDocumentSelector,
    );
  };

  // Note the differences between these selectors and
  // selectClauseTitle/selectClauseBody:
  //  selectClauseTitle/Body:
  //   * (for bodies) Returns formatted text, in Markdown
  //   * Is only updated on blur, or on an outline edit action
  //   * Is used by OutlinerClause to get the saved clause state
  //  selectClauseEdited(Body|Title)Text:
  //   * Returns plain text, no formatting
  //   * Is updated on every keypress
  //   * Is used by the definition search system to keep links up-to-date
  selectClauseEditedTitleText = (
    state,
    documentId,
    clauseId,
    initialDocumentSelector,
  ) => {
    const editedText = this.selectTitleTextEditsForClause(
      state,
      documentId,
      clauseId,
    );
    if (typeof editedText === 'string') {
      return editedText;
    }
    return (
      this.selectClauseTitle(
        state,
        documentId,
        clauseId,
        initialDocumentSelector,
      )?.text || ''
    );
  };
  selectClauseEditedBodyText = (
    state,
    documentId,
    clauseId,
    initialDocumentSelector,
  ) => {
    const editedText = this.selectBodyTextEditsForClause(
      state,
      documentId,
      clauseId,
    );
    if (typeof editedText === 'string') {
      return editedText;
    }
    return (
      this.selectClauseBodyAsDraftRaw(
        state,
        documentId,
        clauseId,
        initialDocumentSelector,
      )?.blocks?.[0]?.text || ''
    );
  };

  selectClauseChildren = (
    state,
    documentId,
    clauseId,
    initialDocumentSelector,
  ) =>
    this.selectClause(state, documentId, clauseId, initialDocumentSelector)
      ?.children;

  selectForceFocusForContentState = (state, documentId) =>
    this.selectEditState(state, documentId)?.forceFocusForContentState;
  selectForceFocusAtEnd = (state, documentId) =>
    !!this.selectEditState(state, documentId)?.forceFocusAtEnd;

  selectClauseForceClearComponentState = (
    state,
    documentId,
    clauseId,
    initialDocumentSelector,
  ) =>
    this.selectClause(state, documentId, clauseId, initialDocumentSelector)
      ?.forceClearComponentState;

  // Returns a new object every time, so must be used with a shallowEqual
  // equality test to avoid rendering on every store update
  selectClauseChildKeyMap = (
    state,
    documentId,
    clauseId,
    initialDocumentSelector,
  ) =>
    this.selectClauseChildren(
      state,
      documentId,
      clauseId,
      initialDocumentSelector,
    ).reduce((mappedIds, childClauseId) => {
      const keyRevision = this.selectClauseForceClearComponentState(
        state,
        documentId,
        childClauseId,
        initialDocumentSelector,
      );
      if (keyRevision) {
        return {
          ...mappedIds,
          [childClauseId]: `${childClauseId}:${keyRevision}`,
        };
      }
      return mappedIds;
    }, {});

  // Walk the document tree to find basic info about all clauses
  findAllClausesFromRoot = (
    clauseId,
    originalClausesById,
    clauseEditsById,
    level = 0,
  ) => {
    const clause = resolveEditedClause(
      clauseId,
      originalClausesById,
      clauseEditsById,
    );
    const allClauseData = clause.children.reduce(
      (prevClauseIds, childClauseId) => [
        ...prevClauseIds,
        ...this.findAllClausesFromRoot(
          childClauseId,
          originalClausesById,
          clauseEditsById,
          level + 1,
        ),
      ],
      [
        {
          clauseId,
          level,
          hasTitle: !!clause.title?.text,
          hasBody: !!clause.body?.text,
        },
      ],
    );
    // if (level === 0) {
    //   console.debug({ findAllClausesFromRoot: allClauseData });
    // }
    return allClauseData;
  };
  // This implements a two-level chache
  // L1 (createCachedSelector):
  //   key = documentId, unlimited size
  //   (cache the clause ID list per document)
  // L2 (createSelector internally)
  //   key = document edit state (multiple keys), size = 1
  //   (recalculate list whenever edit state changes)
  selectAllClausesForDocument = createCachedSelector(
    this.selectRootClauseId,
    this.selectInitialDocumentClausesById,
    this.selectEditedDocumentClausesById,
    this.findAllClausesFromRoot,
  )({
    keySelector: (
      state,
      documentId,
      // initialDocumentSelector
    ) => documentId,
    cacheObject: new FlatMapCache(),
  });

  resolveClauseValidationData = (
    allClauses,
    titleTextEditsByClauseId,
    bodyTextEditsByClauseId,
  ) => {
    const validationData = (allClauses || []).map((clauseData) => {
      let hasTitle = clauseData.hasTitle;
      const editedTitle = titleTextEditsByClauseId?.[clauseData.clauseId];
      if (typeof editedTitle === 'string') {
        hasTitle = !!editedTitle;
      }
      let hasBody = clauseData.hasBody;
      const editedBody = bodyTextEditsByClauseId?.[clauseData.clauseId];
      if (typeof editedBody === 'string') {
        hasBody = !!editedBody;
      }
      return {
        ...clauseData,
        hasTitle,
        hasBody,
      };
    });
    // console.debug({ resolveClauseValidationData: validationData });
    return validationData;
  };
  // This implements a two-level chache
  // L1 (createCachedSelector):
  //   key = documentId, unlimited size
  //   (cache the validation data per document)
  // L2 (createSelector internally)
  //   key = document edit state (multiple keys), size = 1
  //   (update the data once for each edit)
  selectAllClausesForValidation = createCachedSelector(
    this.selectAllClausesForDocument,
    this.selectTitleTextEditsByClauseId,
    this.selectBodyTextEditsByClauseId,
    this.resolveClauseValidationData,
  )({
    keySelector: (
      state,
      documentId,
      // initialDocumentSelector
    ) => documentId,
    cacheObject: new FlatMapCache(),
  });

  selectNumberOfVisibleChapters = (
    state,
    documentId,
    initialDocumentSelector,
  ) => {
    const rootClauseId = this.selectRootClauseId(
      state,
      documentId,
      initialDocumentSelector,
    );
    if (!rootClauseId) {
      return 0;
    }
    const chapterIds = this.selectClauseChildren(
      state,
      documentId,
      rootClauseId,
      initialDocumentSelector,
    );
    if (!chapterIds?.length) {
      return 0;
    }
    if (chapterIds.length > 1) {
      return chapterIds.length;
    }
    const firstChapterClauseId = chapterIds[0];
    const firstChapter = this.selectClause(
      state,
      documentId,
      firstChapterClauseId,
      initialDocumentSelector,
    );
    if (firstChapter?.body?.text || firstChapter.showPreamble) {
      // Just one chapter means only a preamble is shown
      return 1;
    }
    return 0;
  };

  //
  // Action creators
  //

  collapseClauseAction(documentId, clauseId, isCollapsed) {
    return {
      type: COLLAPSE_CLAUSE,
      payload: {
        documentId,
        clauseId,
        isCollapsed,
      },
    };
  }
  collapseClause(documentId, clauseId, isCollapsed) {
    return this.tokenizedApi.state.dispatch(
      this.collapseClauseAction(documentId, clauseId, isCollapsed),
    );
  }

  collapseAllClausesAction(
    initialDocumentSelector,
    documentId,
    fromClauseId,
    isCollapsed,
  ) {
    // This is a (synchronous) thunk, with access to the whole state
    return (dispatch, getState) => {
      const initialDocument = initialDocumentSelector(getState(), documentId);
      const newDocumentEdits = this.selectEditState(getState(), documentId);
      const clauseIds = findAllCollapsibleClauseIdsFromRoot(
        fromClauseId,
        initialDocument?.clausesById,
        newDocumentEdits?.clausesById,
      );
      dispatch({
        type: COLLAPSE_CLAUSES,
        payload: {
          documentId,
          clauseIds,
          isCollapsed,
        },
      });
    };
  }
  collapseAllClauses(
    initialDocumentSelector,
    documentId,
    fromClauseId,
    isCollapsed,
  ) {
    return this.tokenizedApi.state.dispatch(
      this.collapseAllClausesAction(
        initialDocumentSelector,
        documentId,
        fromClauseId,
        isCollapsed,
      ),
    );
  }

  clearEditStateAction(documentId) {
    return {
      type: CLEAR_EDIT_STATE,
      payload: {
        documentId,
      },
    };
  }
  clearEditState(documentId) {
    return this.tokenizedApi.state.dispatch(
      this.clearEditStateAction(documentId),
    );
  }

  editTitleTextAction(documentId, clauseId, text) {
    return {
      type: EDIT_TITLE_TEXT,
      payload: {
        documentId,
        clauseId,
        text,
      },
    };
  }
  editTitleText(documentId, clauseId, text) {
    return this.tokenizedApi.state.dispatch(
      this.editTitleTextAction(documentId, clauseId, text),
    );
  }

  editBodyTextAction(documentId, clauseId, text) {
    return {
      type: EDIT_BODY_TEXT,
      payload: {
        documentId,
        clauseId,
        text,
      },
    };
  }
  editBodyText(documentId, clauseId, text) {
    return this.tokenizedApi.state.dispatch(
      this.editBodyTextAction(documentId, clauseId, text),
    );
  }

  setCursorSelectionAction(
    documentId,
    clauseId,
    linkToClauseId,
    selectionOffsetInBody,
  ) {
    return {
      type: SET_CURSOR_SELECTION,
      payload: {
        documentId,
        clauseId,
        linkToClauseId,
        selectionOffsetInBody,
      },
    };
  }
  setCursorSelection(
    documentId,
    clauseId,
    linkToClauseId,
    selectionOffsetInBody,
  ) {
    return this.tokenizedApi.state.dispatch(
      this.setCursorSelectionAction(
        documentId,
        clauseId,
        linkToClauseId,
        selectionOffsetInBody,
      ),
    );
  }

  clearCursorSelectionAction(documentId, clauseId) {
    return {
      type: CLEAR_CURSOR_SELECTION,
      payload: {
        documentId,
        clauseId,
      },
    };
  }
  clearCursorSelection(documentId, clauseId) {
    return this.tokenizedApi.state.dispatch(
      this.clearCursorSelectionAction(documentId, clauseId),
    );
  }

  setBodyLinkHoverAction(
    documentId,
    clauseId,
    linkToClauseId,
    linkStartOffsetInBody,
    linkEndOffsetInBody,
  ) {
    return {
      type: SET_BODY_LINK_HOVER,
      payload: {
        documentId,
        clauseId,
        linkToClauseId,
        linkStartOffsetInBody,
        linkEndOffsetInBody,
      },
    };
  }
  setBodyLinkHover(
    documentId,
    clauseId,
    linkToClauseId,
    linkStartOffsetInBody,
    linkEndOffsetInBody,
  ) {
    return this.tokenizedApi.state.dispatch(
      this.setBodyLinkHoverAction(
        documentId,
        clauseId,
        linkToClauseId,
        linkStartOffsetInBody,
        linkEndOffsetInBody,
      ),
    );
  }

  clearBodyLinkHoverAction(documentId) {
    return {
      type: CLEAR_BODY_LINK_HOVER,
      payload: {
        documentId,
      },
    };
  }
  clearBodyLinkHover(documentId) {
    return this.tokenizedApi.state.dispatch(
      this.clearBodyLinkHoverAction(documentId),
    );
  }

  prepareForEditingAction = makeEditDocumentStateActionCreator(
    ({
      initialDocument,
      documentId,
      initialClauseAtLevel2,
      ensureInitialEmptyClause,
    }) => ({
      type: PREPARE_FOR_EDITING,
      meta: { initialDocument, documentId },
      payload: { initialClauseAtLevel2, ensureInitialEmptyClause },
    }),
  );
  prepareForEditing({
    initialDocumentSelector,
    saveDocument,
    documentId,
    initialClauseAtLevel2,
    ensureInitialEmptyClause,
  }) {
    return this.tokenizedApi.state.dispatch(
      this.prepareForEditingAction({
        initialDocumentSelector,
        saveDocument,
        documentId,
        initialClauseAtLevel2,
        ensureInitialEmptyClause,
      }),
    );
  }

  showFirstChapterAction = makeEditDocumentStateActionCreator(
    ({ initialDocument, documentId }) => ({
      type: SHOW_FIRST_CHAPTER,
      meta: { initialDocument, documentId },
      payload: {},
    }),
  );
  showFirstChapter({ initialDocumentSelector, saveDocument, documentId }) {
    return this.tokenizedApi.state.dispatch(
      this.showFirstChapterAction({
        initialDocumentSelector,
        saveDocument,
        documentId,
      }),
    );
  }

  hideFirstChapterAction = makeEditDocumentStateActionCreator(
    ({ initialDocument, documentId }) => ({
      type: HIDE_FIRST_CHAPTER,
      meta: { initialDocument, documentId },
      payload: {},
    }),
  );
  hideFirstChapter({ initialDocumentSelector, saveDocument, documentId }) {
    return this.tokenizedApi.state.dispatch(
      this.hideFirstChapterAction({
        initialDocumentSelector,
        saveDocument,
        documentId,
      }),
    );
  }

  divideIntoChaptersAction = makeEditDocumentStateActionCreator(
    ({ initialDocument, documentId }) => ({
      type: DIVIDE_INTO_CHAPTERS,
      meta: { initialDocument, documentId },
      payload: {},
    }),
  );
  divideIntoChapters({ initialDocumentSelector, saveDocument, documentId }) {
    return this.tokenizedApi.state.dispatch(
      this.divideIntoChaptersAction({
        initialDocumentSelector,
        saveDocument,
        documentId,
      }),
    );
  }

  addChapterBeforeAction = makeEditDocumentStateActionCreator(
    ({ initialDocument, documentId, chapterId }) => ({
      type: ADD_CHAPTER_BEFORE,
      meta: { initialDocument, documentId },
      payload: { chapterId },
    }),
  );
  addChapterBefore({
    initialDocumentSelector,
    saveDocument,
    documentId,
    chapterId,
  }) {
    return this.tokenizedApi.state.dispatch(
      this.addChapterBeforeAction({
        initialDocumentSelector,
        saveDocument,
        documentId,
        chapterId,
      }),
    );
  }

  addChapterAfterAction = makeEditDocumentStateActionCreator(
    ({ initialDocument, documentId, chapterId }) => ({
      type: ADD_CHAPTER_AFTER,
      meta: { initialDocument, documentId },
      payload: { chapterId },
    }),
  );
  addChapterAfter({
    initialDocumentSelector,
    saveDocument,
    documentId,
    chapterId,
  }) {
    return this.tokenizedApi.state.dispatch(
      this.addChapterAfterAction({
        initialDocumentSelector,
        saveDocument,
        documentId,
        chapterId,
      }),
    );
  }

  updateClauseAction = makeEditDocumentStateActionCreator(
    ({ initialDocument, documentId, clauseId, edits }) => ({
      type: UPDATE_CLAUSE,
      meta: { initialDocument, documentId },
      payload: { clauseId, edits },
    }),
  );
  updateClause({
    initialDocumentSelector,
    saveDocument,
    documentId,
    clauseId,
    edits,
  }) {
    return this.tokenizedApi.state.dispatch(
      this.updateClauseAction({
        initialDocumentSelector,
        saveDocument,
        documentId,
        clauseId,
        edits,
      }),
    );
  }

  updateClausesAction = makeEditDocumentStateActionCreator(
    ({ initialDocument, documentId, edits }) => ({
      type: UPDATE_CLAUSES,
      meta: { initialDocument, documentId },
      payload: { edits },
    }),
  );
  updateClauses({ initialDocumentSelector, saveDocument, documentId, edits }) {
    return this.tokenizedApi.state.dispatch(
      this.updateClausesAction({
        initialDocumentSelector,
        saveDocument,
        documentId,
        edits,
      }),
    );
  }

  moveCursorFromClauseAction = makeEditDocumentStateActionCreator(
    ({
      initialDocument,
      documentId,
      fromClauseId,
      upOrDown,
      titleOrBody,
      startOrEnd,
      clauseEdits,
    }) => ({
      type: MOVE_CURSOR_FROM_CLAUSE,
      meta: { initialDocument, documentId },
      payload: { fromClauseId, upOrDown, titleOrBody, startOrEnd, clauseEdits },
    }),
  );
  moveCursorFromClause({
    initialDocumentSelector,
    saveDocument,
    documentId,
    fromClauseId,
    upOrDown,
    titleOrBody,
    startOrEnd,
    clauseEdits,
  }) {
    return this.tokenizedApi.state.dispatch(
      this.moveCursorFromClauseAction({
        initialDocumentSelector,
        saveDocument,
        documentId,
        fromClauseId,
        upOrDown,
        titleOrBody,
        startOrEnd,
        clauseEdits,
      }),
    );
  }

  insertClausesAfterAction = makeEditDocumentStateActionCreator(
    ({
      initialDocument,
      documentId,
      insertAfterClauseId,
      clauseEdits,
      newClauses,
      forceFocusForContentState,
      forceFocusAtEnd,
      preventChildInsertion,
    }) => ({
      type: INSERT_CLAUSES_AFTER,
      meta: { initialDocument, documentId },
      payload: {
        insertAfterClauseId,
        clauseEdits,
        newClauses,
        forceFocusForContentState,
        forceFocusAtEnd,
        preventChildInsertion,
      },
    }),
  );
  insertClausesAfter({
    initialDocumentSelector,
    saveDocument,
    documentId,
    insertAfterClauseId,
    clauseEdits,
    newClauses,
    forceFocusForContentState,
    forceFocusAtEnd,
    preventChildInsertion,
  }) {
    return this.tokenizedApi.state.dispatch(
      this.insertClausesAfterAction({
        initialDocumentSelector,
        saveDocument,
        documentId,
        insertAfterClauseId,
        clauseEdits,
        newClauses,
        forceFocusForContentState,
        forceFocusAtEnd,
        preventChildInsertion,
      }),
    );
  }

  indentClauseRightAction = makeEditDocumentStateActionCreator(
    ({
      initialDocument,
      documentId,
      indentClauseId,
      maxIndentLevel,
      clauseEdits,
      forceFocusForContentState,
      forceFocusAtEnd,
    }) => ({
      type: INDENT_CLAUSE_RIGHT,
      meta: { initialDocument, documentId },
      payload: {
        indentClauseId,
        maxIndentLevel,
        clauseEdits,
        forceFocusForContentState,
        forceFocusAtEnd,
      },
    }),
  );
  indentClauseRight({
    initialDocumentSelector,
    saveDocument,
    documentId,
    indentClauseId,
    maxIndentLevel,
    clauseEdits,
    forceFocusForContentState,
    forceFocusAtEnd,
  }) {
    return this.tokenizedApi.state.dispatch(
      this.indentClauseRightAction({
        initialDocumentSelector,
        saveDocument,
        documentId,
        indentClauseId,
        maxIndentLevel,
        clauseEdits,
        forceFocusForContentState,
        forceFocusAtEnd,
      }),
    );
  }

  indentClauseLeftAction = makeEditDocumentStateActionCreator(
    ({
      initialDocument,
      documentId,
      indentClauseId,
      clauseEdits,
      forceFocusForContentState,
      forceFocusAtEnd,
    }) => ({
      type: INDENT_CLAUSE_LEFT,
      meta: { initialDocument, documentId },
      payload: {
        indentClauseId,
        clauseEdits,
        forceFocusForContentState,
        forceFocusAtEnd,
      },
    }),
  );
  indentClauseLeft({
    initialDocumentSelector,
    saveDocument,
    documentId,
    indentClauseId,
    clauseEdits,
    forceFocusForContentState,
    forceFocusAtEnd,
  }) {
    return this.tokenizedApi.state.dispatch(
      this.indentClauseLeftAction({
        initialDocumentSelector,
        saveDocument,
        documentId,
        indentClauseId,
        clauseEdits,
        forceFocusForContentState,
        forceFocusAtEnd,
      }),
    );
  }

  deleteClauseAction = makeEditDocumentStateActionCreator(
    ({ initialDocument, documentId, deleteClauseId, moveCursorUp }) => ({
      type: DELETE_CLAUSE,
      meta: { initialDocument, documentId },
      payload: { deleteClauseId, moveCursorUp },
    }),
  );
  deleteClause({
    initialDocumentSelector,
    saveDocument,
    documentId,
    deleteClauseId,
    moveCursorUp,
  }) {
    return this.tokenizedApi.state.dispatch(
      this.deleteClauseAction({
        initialDocumentSelector,
        saveDocument,
        documentId,
        deleteClauseId,
        moveCursorUp,
      }),
    );
  }

  async updateSavedDraftClauses(
    profileId,
    contractId,
    updatedDocumentForSave,
    cleanForPosting,
  ) {
    const chapters = serializeContractClauses(updatedDocumentForSave).Chapters;
    return await this.tokenizedApi.contracts.updateContractDraft(
      profileId,
      contractId,
      undefined,
      chapters,
      undefined,
      cleanForPosting,
    );
  }

  async updateSavedDraftDefinitions(
    profileId,
    contractId,
    updatedDocumentForSave,
    cleanForPosting,
  ) {
    const definitions = serializeContractDefinitions(
      updatedDocumentForSave,
    ).Definitions;
    return await this.tokenizedApi.contracts.updateContractDraft(
      profileId,
      contractId,
      undefined,
      undefined,
      definitions,
      cleanForPosting,
    );
  }

  // For performance reasons, links in the Markdown body text
  // output by the Outliner are only updated for individual
  // clauses when they’re edited. So when changes are made to
  // links across the document, although the rendered links all update
  // immediately, the links in the saved Markdown can
  // become stale. This async action is provided to update *all*
  // the links across every clause. This is an expensive, non-interactive
  // operation, and should be used sparingly – e.g. before signing
  // a contract. Note that further edits must be prevented while
  // the action is running.
  updateAllEditedLinks(profileId, contractId, contract) {
    return this.tokenizedApi.state.dispatch(
      this.updateAllEditedLinksAction(profileId, contractId, contract),
    );
  }
  updateAllEditedLinksAction(profileId, contractId, contract) {
    return async (dispatch, getState) => {
      if (contract?.draft?.details?.bodyOfAgreementType !== 2) {
        return;
      }

      const documents = [
        {
          documentId: this.makeDocumentIdForClauses(profileId, contractId),
          initialDocumentSelector: this.makeInitialDocumentSelectorForClauses(
            profileId,
            contractId,
            contract,
            true,
          ),
          saveDocument: ({ updatedDocumentForSave }) =>
            this.updateSavedDraftClauses(
              profileId,
              contractId,
              updatedDocumentForSave,
              true,
            ),
        },
        {
          documentId: this.makeDocumentIdForDefinitions(profileId, contractId),
          initialDocumentSelector:
            this.makeInitialDocumentSelectorForDefinitions(
              profileId,
              contractId,
              contract,
              true,
            ),
          saveDocument: ({ updatedDocumentForSave }) =>
            this.updateSavedDraftDefinitions(
              profileId,
              contractId,
              updatedDocumentForSave,
              true,
            ),
        },
      ];

      for (const {
        documentId,
        initialDocumentSelector,
        saveDocument,
      } of documents) {
        // Step 1: Get a list of clause IDs for the document
        const clausesById = this.selectInitialDocumentClausesById(
          getState(),
          documentId,
          initialDocumentSelector,
        );
        await yieldMainThread();

        // Step 2: Update the links for every body in the document
        const edits = {};
        for (const [clauseId, clause] of Object.entries(clausesById)) {
          if (clause.body?.text) {
            const bodyForDisplay = this.selectClauseBodyAsDraftRaw(
              getState(),
              documentId,
              clauseId,
              initialDocumentSelector,
            );
            await yieldMainThread();
            if (bodyForDisplay) {
              const bodyLinks = this.selectDefinitionLinksForClause(
                getState(),
                contract,
                documentId,
                clauseId,
                initialDocumentSelector,
                true,
              );
              await yieldMainThread();
              const linkOverrides = (bodyLinks.matches || []).map((match) => ({
                offset: match.searchedTextMatchOffset,
                length: match.searchedTextMatchLength,
                definedTerm: match.termSegments[0].input,
              }));
              const markdown = draftRawToMarkdown(
                bodyForDisplay,
                linkOverrides,
                true,
              );
              edits[clauseId] = {
                body: {
                  text: markdown,
                },
              };
              await yieldMainThread();
            }
          }
        }

        // Step 3: Save the document with all the links updated
        await dispatch(
          this.updateClausesAction({
            initialDocumentSelector,
            saveDocument,
            documentId,
            edits,
          }),
        );
        await yieldMainThread();
      }
    };
  }

  async addBodyOfAgreement(profileId, contractId) {
    return await this.tokenizedApi.contracts.updateContractOrInstrumentFields({
      profileId,
      contractId,
      fieldValues: {
        bodyOfAgreementType: 2,
      },
    });
  }

  async deleteBodyOfAgreement(profileId, contractId) {
    return await this.tokenizedApi.contracts.updateContractOrInstrumentFields({
      profileId,
      contractId,
      fieldValues: {
        bodyOfAgreementType: 0,
      },
    });
  }
}

export default ContractProse;
